import {
    combineLatest,
    firstValueFrom,
    Observable,
    Subject,
    switchMap,
} from 'rxjs'
import { distinctUntilChanged, map, tap } from 'rxjs/operators'
import { SearchClient } from 'algoliasearch'

import { Component, Inject, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'

import { ALGOLIA_SEARCH_CLIENT } from '@app-domains/algolia/algolia.module'
import { AlgoliaSortingDirection, AlgoliaSortingProperty, getIndexName } from '@app-lib/algolia.lib'
import { Analytics } from '@app-domains/analytics/types/analytics.types'
import { EmptyState } from '@app-domains/empty-state/types/empty-state'
import { AlgoliaConfig } from '@app-types/product.types'
import { DebtorFragment, PageFragment, PositionedPageEnum } from '@app-graphql/schema'
import { generateDebtorExclusivityFilter } from '@app-lib/debtor.lib'
import { shareReplayOne } from '@app-lib/rxjs.lib'
import { LocalizationService } from '@app-domains/localization/service/localization.service'
import { DebtorService, PageService } from '@app-services'
import { PageState } from '@app-types/common.types'
import { Localization } from '@aa-app-localization'

export type PPageState = PageState<PageFragment>

@Component({
    selector: 'app-page',
    templateUrl: './page.component.html',
})
export class PageComponent implements OnInit {
    public emptyState: EmptyState = {
        icon: 'sofa',
        message: 'catalog-index.no-products',
    }

    public readonly clearRefinementsCommands$ = new Subject<void>()
    public listType: Analytics.ListType
    public algolia$: Observable<AlgoliaConfig>
    public noRights = false

    public pageIdentifier: PositionedPageEnum
    public pageIsRange: boolean = false
    public slug: string = ''

    constructor(
        private readonly route: ActivatedRoute,
        public localization: LocalizationService,
        @Inject(ALGOLIA_SEARCH_CLIENT)
        private readonly searchClient: SearchClient,
        private readonly debtorService: DebtorService,
        public readonly pageService: PageService,
    ) {
    }

    public ngOnInit(): void {
        this.slug = this.route.snapshot.params.slug


        this.route.params
            .pipe(
                map(params => params.slug),
                tap(params => this.slug = params),
                distinctUntilChanged(),
                switchMap(slug => this.pageService.getIdentifierFromSlug$(slug)),
            )
            .subscribe(identifier => {
                if (! identifier) {
                    console.error('No matching PositionedPageEnum found')
                    return
                }

                this.pageIdentifier = identifier
                this.pageIsRange = this.pageIdentifier === PositionedPageEnum.RangePage
            })

        firstValueFrom(this.debtorService.debtor$).then((debtorRights) => {
            if (debtorRights
                && (! debtorRights.debtorRights.allowedBrands.length
                    || ! debtorRights.debtorRights.allowedProductGroups.length)
            ) {
                this.noRights = true
            } else {
                this.algolia$ = combineLatest([
                    this.debtorService.debtor$,
                    this.localization.currentLocale$,
                ]).pipe(
                    tap(() => {
                        this.clearRefinementsCommands$.next()
                        this.listType = 'products'
                    }),
                    map(([debtor, locale]) => this.getAlgoliaConfig(locale, debtor)),
                    shareReplayOne(),
                ) as Observable<AlgoliaConfig>
            }
        })
    }

    private createFilterForRange = (debtor?: DebtorFragment): string => `type:"product" AND (range:"${this.slug}") ${generateDebtorExclusivityFilter(debtor)}`

    private getAlgoliaConfig = (
        locale: Localization.Locale,
        debtor?: DebtorFragment,
    ): AlgoliaConfig => {
        const filters = this.createFilterForRange(debtor)

        const indexName = getIndexName({
            locale,
            sorting: {
                property: AlgoliaSortingProperty.CreationDate,
                direction: AlgoliaSortingDirection.Descending,
            },
        })

        return {
            searchParameters: {
                hitsPerPage: 62,
                filters,
            },
            instantSearchConfig: {
                indexName,
                searchClient: this.searchClient,
            },
        }
    }

}
